/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
/**
 * @description This method checks whether the pageName consists of a pagelist and returns an array by spliting the pageName into
 * pageName, pageListName and index if pageList's name is present in pageName.
 * @example <caption>Example for getPageListNameAndIndexFromPageName()</caption>
 * // In this example, the API checks if the contextName passed exists as a root Node
 * getPageListNameAndIndexFromPageName("caseInfo.content.Employee[0]")
 * // returns ["caseInfo.content","Employee","0"] An array consisting of [pageName, pageListName, index]
 * @param contextName Name of the context we are checking for
 * @returns An array consisting of [pageName, pageListName, index] or [pageName] if pageName doesn't consists of a pagelist
 * @function
 * @private
 */
const pageListRegex = /(([^.]+)\[\d+\])/g;

const getPageListNameAndIndexFromPageName = (pageName?: string) => {
  const array = pageName?.split('.') ?? [];
  const minimumSplitsForPageList = 3;
  if (array.length < minimumSplitsForPageList) {
    return [pageName];
  }
  const pageLists = pageName?.match(pageListRegex);
  if (!pageLists) {
    return [pageName];
  }

  let pageListName = pageLists[pageLists.length - 1];
  pageName = pageName?.split(pageListName)[0];
  pageName = pageName?.substring(0, pageName.length - 1);

  let index;
  [pageListName, index] = pageListName.split('[');
  index = index?.split(']')[0];
  return [pageName, pageListName, index];
};

/**
 * Gets the first array index in the `pageListFullPath` string that matches the start of the `pageListName` string.
 *
 * @param {string} pageListFullPath - The full path string that may contain an array index.
 * @param {string} pageListName - The prefix string to match the start of the `pageListFullPath` string.
 * @returns {number} - The first array index if a match is found; otherwise, returns -1.
 *
 * @example
 * // Returns 0
 * getPageListIndex('caseInfo.content.EditionInformation.IncludedList[0].StandardPricelist', 'caseInfo.content.EditionInformation.IncludedList');
 *
 * @example
 * // Returns 2
 * getPageListIndex('caseInfo.content.EditionInformation.IncludedList[2].Details[0].Price', 'caseInfo.content.EditionInformation.IncludedList[2].Details');
 *
 * @example
 * // Returns -1
 * getPageListIndex('caseInfo.content.EditionInformation.IncludedList.Details.Price', 'caseInfo.content.EditionInformation.IncludedList');
 */
const getPageListIndex = (pageListFullPath: string, pageListName: string): number => {
  if (!pageListFullPath.startsWith(pageListName)) {
    return -1;
  }

  // Remove the pageListName part from pageListFullPath to work with the remaining path.
  const remainingPath = pageListFullPath.slice(pageListName.length);

  // Match the first array index in the remaining path.
  const regex = /\[([0-9]+)\]/;
  const matches = remainingPath.match(regex);

  if (matches) {
    return parseInt(matches[1], 10);
  }

  return -1; // Return -1 if no match is found
};

export default { getPageListNameAndIndexFromPageName, getPageListIndex };
